import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
} from '@angular/core';
import { Alias, Mailbox } from '../../models/mailboxes.model';
import * as AliasActions from '../../store/actions/alias.actions';
import {
  ApplicationState,
  getAliasesForSelectedMailbox,
  getAliasFailure,
  getAliasSuccess,
  getSelectedMailbox,
} from '../../../store/reducers';
import { InceptionService, TranslationService } from '@proximus/ng-common';
import { Store } from '@ngrx/store';
import { Alert, ModalService } from '@proximus/ng-common-components';
import { SuccessCode, SuccessInfo } from '../../models/success.model';
import { ErrorCode, TechnicalError } from '../../models/errors.model';
import { AlertsUtilService } from '../../service/alerts.util.service';
import { ConfirmDeleteAliasComponent } from './confirm-delete-alias.component';
import { Observable, of } from 'rxjs';
import { combineLatest, filter, map } from 'rxjs/operators';

@Component({
  selector: 'mprd-aliases-container',
  templateUrl: 'aliases-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AliasesContainerComponent implements OnInit {
  selectedMailbox$: Observable<Mailbox>;
  aliases$: Observable<Alias[]>;
  remainingAliases$: Observable<number>;
  remainingAliasesLabel$: Observable<string>;
  alerts$: Observable<Alert[]>;
  alerts: Alert[] = [];
  showLoadingSpinner$: Observable<boolean>;

  constructor(
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
    private alertsUtilService: AlertsUtilService,
    private changeDetector: ChangeDetectorRef,
    private modalService: ModalService,
    private inceptionService: InceptionService,
    private elementRef: ElementRef,
  ) {
    this.aliases$ = store.select(getAliasesForSelectedMailbox);
    this.selectedMailbox$ = store.select(getSelectedMailbox);

    this.remainingAliases$ = this.aliases$.pipe(
      combineLatest(
        this.selectedMailbox$,
        (aliases: Alias[], mailbox: Mailbox) => {
          if (aliases && mailbox) {
            return mailbox.maxAliases - aliases.length;
          } else if (mailbox) {
            return mailbox.maxAliases;
          } else {
            return 0;
          }
        },
      ),
    );

    this.remainingAliasesLabel$ = this.remainingAliases$.pipe(
      map((remainingAliases: number) =>
        translationService
          .getTranslatedValueFor('aliases.aliases.remaining')
          .replace(/\{0\}/g, remainingAliases.toString()),
      ),
    );

    this.store
      .select(getSelectedMailbox)
      .pipe(filter(Boolean))
      .subscribe((selectedMailbox: Mailbox) => {
        this.clearErrorAlerts();
        this.clearSuccessAlerts();
        this.store.dispatch(new AliasActions.ResetSuccess());
        this.store.dispatch(new AliasActions.ResetError());
      });

    this.showLoadingSpinner$ = of(true);
    this.store
      .select(getAliasSuccess)
      .pipe(filter(Boolean))
      .subscribe((success: SuccessInfo) => {
        this.showLoadingSpinner$ = of(false);
        this.updateAliasSuccess(success);
      });
    this.store
      .select(getAliasFailure)
      .pipe(filter(Boolean))
      .subscribe((error: TechnicalError) => {
        this.showLoadingSpinner$ = of(false);
        this.updateAliasFailures(error);
      });
    this.alerts$ = of(this.alerts);
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.inceptionService.panelAccordion(this.elementRef);
      this.inceptionService.init();
    });
  }

  private clearSuccessAlerts() {
    this.removeAlertByCode(SuccessCode.AliasCreated);
    this.removeAlertByCode(SuccessCode.AliasRemoved);
  }

  private clearErrorAlerts() {
    this.removeAlertByCode(ErrorCode.MailboxAliasMalformed);
    this.removeAlertByCode(ErrorCode.MailboxAliasMaxNumberExceeded);
    this.removeAlertByCode(ErrorCode.MailboxAliasNotUnique);
    this.removeAlertByCode(ErrorCode.AliasUnknown);
    this.removeAlertByCode(ErrorCode.TechnicalError);
  }

  onCreateAlias(aliasId: string) {
    this.store.dispatch(new AliasActions.CreateAction(aliasId));
  }

  onAlerts(alerts: Alert[]) {
    this.alerts$ = of(alerts);
  }

  onRemoveAlias(aliasId: string) {
    this.modalService
      .create<ConfirmDeleteAliasComponent>(ConfirmDeleteAliasComponent)
      .subscribe((componentRef) => {
        componentRef.instance.aliasId = aliasId;
      });
  }

  private updateAliasFailures(technicalError: TechnicalError) {
    switch (technicalError.code) {
      case ErrorCode.MailboxAliasMalformed:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'alias.creation.failure.malformed.message',
            technicalError.code,
          ),
        );
        break;
      case ErrorCode.MailboxAliasMaxNumberExceeded:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'alias.creation.failure.maximum.aliases.message',
            technicalError.code,
          ),
        );
        break;
      case ErrorCode.MailboxAliasNotUnique:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'alias.creation.failure.not.unique.message',
            technicalError.code,
          ),
        );
        break;
      case ErrorCode.AliasUnknown:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'alias.removal.failure.unknown.alias.message',
            technicalError.code,
          ),
        );
        break;
    }
    if (technicalError.code === undefined) {
      this.addAlert(
        this.alertsUtilService.getAlertFor(
          'error',
          false,
          'general.technical.error.message',
          ErrorCode.TechnicalError,
        ),
      );
    }
    this.clearSuccessAlerts();
  }

  private updateAliasSuccess(success: SuccessInfo) {
    switch (success.code) {
      case SuccessCode.AliasCreated:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'success',
            false,
            'alias.creation.success.message',
            success.code,
          ),
        );
        this.removeAlertByCode(SuccessCode.AliasRemoved);
        break;
      case SuccessCode.AliasRemoved:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'success',
            false,
            'alias.removal.success.message',
            success.code,
          ),
        );
        this.removeAlertByCode(SuccessCode.AliasCreated);
    }
    this.clearErrorAlerts();
  }

  private removeAlertByCode(code: SuccessCode | ErrorCode): void {
    if (this.alerts.length > 0) {
      const previousAlerts = this.alerts.filter((alert) => alert.id === code);
      if (previousAlerts.length > 0) {
        this.alerts.splice(this.alerts.indexOf(previousAlerts[0]), 1);
      }
    }
    this.changeDetector.markForCheck();
  }

  private addAlert(alert: Alert) {
    if (
      alert &&
      !this.alerts.find((existingAlert) => existingAlert.id === alert.id)
    ) {
      this.alerts.push(alert);
    }
    this.changeDetector.markForCheck();
  }

  onCloseAlert(alert: Alert) {
    switch (alert.type) {
      case 'success':
        this.store.dispatch(new AliasActions.ResetSuccess());
        break;
      case 'error':
        this.store.dispatch(new AliasActions.ResetError());
        break;
    }
  }
}
